/**
 * Unified Spell Casting Service
 *
 * Centralizes all spell casting logic for:
 * 1. Spellbook quickcast
 * 2. Magic item casting
 * 3. Custom spell casting
 *
 * Ensures consistent behavior, component checking, resource consumption,
 * rolling, and activity logging across all casting interfaces.
 *
 * [INTEGRATION 2025-10-23]: Now emits systemBus events for spell casting
 * [WHY]: Allows other systems to react to spells (concentration tracking, etc.)
 */

import { rollDice } from '../utils/diceParser';
import { effectCalculator } from './effectCalculator';
import { effectRegistry } from './effectRegistry';
import { systemBus, EVENTS } from './systemBus';
import { autoPurchase } from '../utils/currencyUtils';
import { concentrationSystem } from './concentrationSystem'; // [PHASE 0.2 - NOV 11, 2025]: Concentration tracking
import { parseDurationToMinutes } from '../utils/spellDurationParser';
import { consumePerRestSpellUse } from '../utils/perRestSpellUsage';
import { spendResource } from '../utils/resourceCostSpellUsage';
import { consumeMaterialComponents } from './spellComponentConsumption'; // [Tier 1.15 — APR 2026]: Honor componentDetails.consumed flag from CustomSpellCreator
import { hasArmorProficiency, hasShieldProficiency } from '../utils/armorProficiency'; // [Spec 1b — Phase B / #327]: Armor proficiency penalty helpers

/**
 * [Spec 1b — Phase B / #327]: PHB p.144 — cannot cast spells while wearing
 * non-proficient armor or wielding a non-proficient shield. Hard block;
 * player must doff before casting.
 *
 * Flag-gated by settings.enableArmorProficiencyPenalties. When the flag is
 * off (the default), this function always returns { ok: true } so existing
 * behaviour is completely unchanged.
 *
 * @param {Object} character - Character data (inventory, proficiencies)
 * @param {Object} _spell    - Spell being cast (reserved for future gates)
 * @param {Object} settings  - App settings ({ enableArmorProficiencyPenalties })
 * @returns {{ ok: boolean, reason?: string }}
 */
export function canCast(character, _spell, settings) {
  if (settings?.enableArmorProficiencyPenalties) {
    const inv = character?.inventory || [];

    const equippedArmor = inv.find(
      i => i.equipped && i.armorType && i.armorType !== 'shield' && i.armorType !== 'unarmored'
    );
    if (equippedArmor && !hasArmorProficiency(character, equippedArmor)) {
      return { ok: false, reason: 'Cannot cast — wearing non-proficient armor (PHB p.144).' };
    }

    const equippedShield = inv.find(i => i.equipped && i.armorType === 'shield');
    if (equippedShield && !hasShieldProficiency(character, equippedShield)) {
      return { ok: false, reason: 'Cannot cast — wielding non-proficient shield (PHB p.144).' };
    }
  }
  // Future: additional gates here (concentration check, slot availability, etc.)
  return { ok: true };
}

/**
 * Check if a spell can be cast (components available, resources available, etc.)
 *
 * @param {Object} spell - The spell object
 * @param {Object} context - Context with inventory, checkSpellComponents, etc.
 * @returns {Object} - { canCast: boolean, message: string, componentCheck: Object }
 */
export const canCastSpell = (spell, context) => {
  const { checkSpellComponents, spellSlots, item, castAsRitual, isAtWill } = context;

  // Skip component check for cantrips
  if (spell.level === 0) {
    return { canCast: true, message: '', componentCheck: null };
  }

  // Check components
  const componentCheck = checkSpellComponents(spell);

  if (!componentCheck.canCast && componentCheck.canCast !== 'needsPurchase') {
    return {
      canCast: false,
      message: componentCheck.message,
      componentCheck
    };
  }

  // Check resource availability
  if (item) {
    // Casting from item - check charges
    const spellData = item.spells?.[context.spellIndex];
    if (!spellData) {
      return { canCast: false, message: 'No charges remaining', componentCheck };
    }
    // Pool-based: item has a shared charge pool and spell declares costPerCast
    if (item.charges != null && item.maxCharges != null && spellData.costPerCast != null) {
      const cost = spellData.costPerCast;
      if (item.charges < cost) {
        return {
          canCast: false,
          message: `Insufficient charges (need ${cost}, have ${item.charges})`,
          componentCheck
        };
      }
    } else if (spellData.currentUses <= 0) {
      // Legacy per-spell counter path
      return {
        canCast: false,
        message: 'No charges remaining',
        componentCheck
      };
    }
  } else if (spell.level > 0 && !castAsRitual && !isAtWill) {
    // [RITUAL CASTING NOV 13, 2025]: Skip slot check if casting as ritual (PHB p.201)
    // [Phase 4b-CW APR 2026]: Skip slot check if at-will (Warlock invocations like
    // beast_speech, eldritch_sight, etc. — registered via effectRegistry atWillSpell type)
    // Casting from spellbook - check slots (UNLESS ritual or at-will)
    const castLevel = context.castLevel || spell.level;
    const hasSlot = spellSlots?.[castLevel] &&
                    spellSlots[castLevel].max > spellSlots[castLevel].used;

    if (!hasSlot) {
      return {
        canCast: false,
        message: `No level ${castLevel} spell slots remaining`,
        componentCheck
      };
    }
  }

  return { canCast: true, message: '', componentCheck };
};

/**
 * Handle component purchasing and consumption
 *
 * @param {Object} spell - The spell object
 * @param {Object} componentCheck - Result from checkSpellComponents
 * @param {Object} context - Context with consumeSpellComponents, currencies, etc.
 * @returns {Promise<boolean>} - Whether components were successfully handled
 */
export const handleComponents = async (spell, componentCheck, context) => {
  const { consumeSpellComponents, setCurrencies, currencies, showStatus, logActivity, confirm } = context;

  // If needs purchase, prompt user
  if (componentCheck?.canCast === 'needsPurchase') {
    // [UI POLISH]: Use styled confirm if available, fallback to window.confirm
    const confirmFn = confirm || (({ message }) => Promise.resolve(window.confirm(message)));
    const userConfirmed = await confirmFn({ title: 'Spell Components', message: componentCheck.message, confirmText: 'Purchase & Cast', confirmColor: 'blue' });

    if (!userConfirmed) {
      return false; // User cancelled
    }

    // Use autoPurchase for standardized currency handling
    const cost = componentCheck.cost;
    const purchaseResult = autoPurchase(cost, currencies);

    if (!purchaseResult.success) {
      showStatus(`❌ ${purchaseResult.message}`, 3000);
      return false;
    }

    // Store old amounts for event emission
    const oldCurrencies = { ...currencies };

    // Update currencies
    setCurrencies(purchaseResult.newCurrency);

    // Emit CURRENCY_CHANGED event for each changed currency type
    ['pp', 'gp', 'sp', 'cp'].forEach(type => {
      if (oldCurrencies[type] !== purchaseResult.newCurrency[type]) {
        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
          type,
          oldAmount: oldCurrencies[type],
          newAmount: purchaseResult.newCurrency[type],
          change: purchaseResult.newCurrency[type] - oldCurrencies[type],
          source: 'spell_components'
        });
      }
    });

    showStatus(`Purchased components for ${cost}gp`);
    logActivity(`Purchased spell components`, 'currency', {
      spell: spell.name,
      cost: cost
    });
  }

  // Consume components if needed
  if (componentCheck && consumeSpellComponents) {
    consumeSpellComponents(spell, componentCheck);
  }

  return true;
};

/**
 * [BATCH 2 / P0 C-1 — APR 2026]: Cantrip damage scales by character level.
 *
 * Per PHB p.201 every damage cantrip adds extra dice at character levels
 * 5 / 11 / 17. The threshold is total character level (Sage Advice
 * Compendium — multiclass characters use the sum of class levels, not
 * a single class level). Result: 1 die / 2 dice / 3 dice / 4 dice.
 *
 * Defensive defaults — undefined, null, 0, or negative levels return
 * tier 1 (the safest "no-scaling" baseline). Out-of-range high values
 * cap at tier 4 (no real character ever exceeds L20, but a custom
 * post-20 epic-tier homebrew shouldn't crash).
 *
 * @param {number} characterLevel
 * @returns {1|2|3|4}
 */
export const getCantripDieMultiplier = (characterLevel) => {
  const lvl = Number.isFinite(characterLevel) ? characterLevel : 0;
  if (lvl >= 17) return 4;
  if (lvl >= 11) return 3;
  if (lvl >= 5) return 2;
  return 1;
};

/**
 * Apply cantrip die-count scaling to a dice formula.
 * '1d10' × 4 → '4d10'. Embedded flat bonuses (e.g. '1d6+1') are preserved
 * unchanged — RAW scales the die count, not the static modifier.
 * Returns the input unchanged if no NdM term is found (defensive).
 *
 * @param {string} diceFormula
 * @param {number} multiplier
 * @returns {string}
 */
const scaleCantripDice = (diceFormula, multiplier) => {
  if (multiplier <= 1 || !diceFormula) return diceFormula || '';
  const m = String(diceFormula).match(/^(\d+)d(\d+)(.*)$/);
  if (!m) return diceFormula;
  const baseCount = parseInt(m[1], 10) || 1;
  const sides = m[2];
  const tail = m[3] || ''; // preserves '+1', '-2', or any extra whitespace/chars
  return `${baseCount * multiplier}d${sides}${tail}`;
};

/**
 * Calculate upcast damage/healing
 *
 * @param {Object} spell - The spell object
 * @param {number} castLevel - The level at which spell is being cast
 * @param {number} [characterLevel] - Total character level (for cantrip scaling).
 *   Optional; cantrip scaling only fires when supplied. Slot-based upcast
 *   ignores this argument.
 * @returns {string} - Dice formula with upcast dice added (or scaled cantrip)
 */
export const calculateUpcastDamage = (spell, castLevel, characterLevel) => {
  const baseDice = spell.damage || spell.healing || '';

  // [BATCH 2 / P0 C-1]: Cantrip scaling. Detected by spell.level === 0.
  //   Eldritch Blast is special-cased elsewhere (its `attacksPerLevel` array
  //   adds beams instead of multiplying dice — see castSpell line ~435).
  //   We deliberately scale here too in case a caller passes Eldritch Blast
  //   to calculateUpcastDamage outside the beam path; the helper just
  //   doubles its 1d10, which matches the per-beam die count.
  //
  //   EXCLUSION: cantrips that lack a saveType are buffs/utility (e.g.
  //   Shillelagh — d8 is the modified weapon's die, not a spell-damage
  //   roll, per Sage Advice Compendium 1.04 p.13). Skip scaling for them
  //   so the buff descriptor stays at its base die.
  if (spell.level === 0 && spell.saveType) {
    const mult = getCantripDieMultiplier(characterLevel);
    return scaleCantripDice(baseDice, mult);
  }

  const levelsAbove = castLevel - spell.level;

  if (levelsAbove <= 0) {
    return baseDice;
  }

  // [FIX]: Check for structured upcast dice (from CustomSpellCreator)
  // Format: { upcastDice: "1d8", upcastDamageType: "fire", upcastStartLevel: 2 }
  if (spell.upcastDice) {
    const startLevel = spell.upcastStartLevel || (spell.level + 1);
    const levelsAboveStart = castLevel - startLevel + 1;

    if (levelsAboveStart > 0) {
      const additionalDice = [];
      for (let i = 0; i < levelsAboveStart; i++) {
        additionalDice.push(spell.upcastDice);
      }
      return additionalDice.length > 0 ? `${baseDice} + ${additionalDice.join(' + ')}` : baseDice;
    }
    return baseDice;
  }

  // Check for explicit upcast damage (legacy format)
  if (spell.upcastDamage) {
    const additionalDice = [];
    for (let i = 0; i < levelsAbove; i++) {
      additionalDice.push(spell.upcastDamage);
    }
    return additionalDice.length > 0 ? `${baseDice} + ${additionalDice.join(' + ')}` : baseDice;
  }

  // Default upcast behavior - add 1 die per level
  if (baseDice && spell.level > 0) {
    const match = baseDice.match(/(\d+)d(\d+)/);
    if (match) {
      const upcastDice = `1d${match[2]}`;
      const additionalDice = [];
      for (let i = 0; i < levelsAbove; i++) {
        additionalDice.push(upcastDice);
      }
      return `${baseDice} + ${additionalDice.join(' + ')}`;
    }
  }

  return baseDice;
};

/**
 * Roll a spell attack
 *
 * @param {Object} context - Context with rollSpellAttack function
 * @returns {Object} - { d20, bonus, total, breakdown, text, isCrit, isFumble, quickBonus, quickBonusText }
 */
export const rollSpellAttack = (context) => {
  const { rollSpellAttack } = context;
  return rollSpellAttack();
};

/**
 * Apply per-die minimum to a roll result. Used for Elemental Adept (PHB p.166):
 * treat any 1 on a damage die as a 2 (for the chosen element).
 *
 * [Phase 4b-infra APR 2026]: rolls are mutated to clamp each die to `minimum`,
 * and the total is recomputed from clamped rolls + modifier.
 *
 * @param {Object} rollResult - object returned by rollDice ({total, rolls, modifier, ...})
 * @param {number} minimum - minimum value per die (0 = no clamp)
 * @returns {Object} new roll result with clamped dice
 */
const applyDieMinimum = (rollResult, minimum) => {
  if (!rollResult || !minimum || minimum <= 0) return rollResult;
  if (!Array.isArray(rollResult.rolls) || rollResult.rolls.length === 0) return rollResult;
  const clampedRolls = rollResult.rolls.map(r => Math.max(r, minimum));
  const totalFromDice = clampedRolls.reduce((sum, r) => sum + r, 0);
  const newTotal = totalFromDice + (rollResult.modifier || 0);
  // Rebuild breakdown showing clamped dice (parenthetical preserved for readability).
  const dice = rollResult.dice;
  let breakdown = rollResult.breakdown;
  if (dice && dice.count > 0 && dice.sides > 0) {
    breakdown = `${dice.count}d${dice.sides}(${clampedRolls.join('+')})`;
    if (dice.modifier !== 0) {
      breakdown += ` ${dice.modifier >= 0 ? '+' : ''}${dice.modifier}`;
    }
  }
  return { ...rollResult, rolls: clampedRolls, total: newTotal, breakdown };
};

/**
 * Roll spell damage or healing
 *
 * @param {Object} spell - The spell object
 * @param {number} castLevel - The level at which spell is being cast
 * @param {Object} context - Context with playerStats, etc.
 * @returns {Object} - { total, dice, rolls, diceGroups, modifier, type, secondary? }
 */
export const rollSpellDamage = (spell, castLevel, context) => {
  const { playerStats } = context;

  const damageField = spell.damage || spell.healing;
  if (!damageField) {
    return null;
  }

  // [BATCH 2 / P0 C-1 — APR 2026]: Cantrip scaling needs character level.
  //   playerStats.level is the canonical total character level (matches the
  //   field used by Eldritch Blast beam-count logic at line ~435 below).
  const characterLevel = playerStats?.level;

  // Calculate upcast damage. Cantrips (level 0) scale by characterLevel,
  // not by castLevel — the second branch handles that inside
  // calculateUpcastDamage.
  const diceToRoll = (castLevel > spell.level || spell.level === 0) ?
    calculateUpcastDamage(spell, castLevel, characterLevel) :
    damageField;

  let damageRoll = rollDice(diceToRoll);

  // Add spellcasting modifier to healing spells
  // [FIX JAN 13, 2026]: Property-based detection instead of string matching (ARC-014)
  let modifier = 0;
  const isHealing = spell.spellType === 'healing' || spell.healing || spell.isHealing || spell.isHealing1;

  if (isHealing) {
    modifier = playerStats.spellcastingModifier || 0;
    if (!modifier && playerStats.spellcastingAbility) {
      // [FIXED 2025-11-11]: Use effectCalculator.getEffectiveModifier directly (includes Rod of the Pact Keeper, etc.)
      modifier = effectCalculator.getEffectiveModifier(
        playerStats,
        context.inventory || [],
        context.activeQuickBonuses || [],
        playerStats.spellcastingAbility
      );
    }

    // [Phase 4b BC-8 — APR 2026]: Cleric Life Domain "Disciple of Life" heal bonus.
    //   PHB p.60 — When you cast a healing spell of 1st level or higher, the target
    //   regains additional 2 + spell level HP. Only applies to leveled spells (not
    //   cantrips like Spare the Dying).
    //   The flag is granted by srdSubclassFeatures.js Cleric Life Domain L1.
    if (
      castLevel >= 1 &&
      effectCalculator.hasFeatureFlag(playerStats, context.inventory || [], 'cleric_life_disciple_heal_bonus')
    ) {
      modifier += 2 + castLevel;
    }
  } else {
    // [Phase 4b-infra APR 2026]: Apply Elemental Adept per-die minimum (PHB p.166).
    // Only applies to non-healing damage rolls of the chosen element.
    const dmgType = spell.damageType;
    if (dmgType) {
      const minimum = effectCalculator.getSpellDamageDieMinimum(
        playerStats,
        context.inventory || [],
        dmgType
      );
      if (minimum > 0) {
        damageRoll = applyDieMinimum(damageRoll, minimum);
      }
    }
  }

  // [FIX]: Roll secondary damage type if present (Issue 8)
  // Spells like custom spells with 3d6 fire + 3d6 necrotic now roll both
  let secondaryDamage = null;
  if (spell.damage2) {
    let secondaryRoll = rollDice(spell.damage2);
    const isHealing2 = spell.isHealing2 || false;

    // [Phase 4b-infra APR 2026]: Apply Elemental Adept per-die minimum to secondary damage too.
    if (!isHealing2 && spell.damageType2) {
      const minimum2 = effectCalculator.getSpellDamageDieMinimum(
        playerStats,
        context.inventory || [],
        spell.damageType2
      );
      if (minimum2 > 0) {
        secondaryRoll = applyDieMinimum(secondaryRoll, minimum2);
      }
    }

    // [FIX APR 2026]: rollDice() returns {breakdown, rolls}, not {text, diceGroups}
    secondaryDamage = {
      total: secondaryRoll.total,
      dice: spell.damage2,
      rolls: secondaryRoll.breakdown,
      diceGroups: secondaryRoll.rolls,
      modifier: 0,
      type: isHealing2 ? 'Healing' : (spell.damageType2 || 'Damage')
    };
  }

  // [FIX APR 2026]: rollDice() returns {breakdown, rolls}, not {text, diceGroups}
  return {
    total: damageRoll.total + modifier,
    dice: diceToRoll,
    rolls: damageRoll.breakdown,
    diceGroups: damageRoll.rolls,
    modifier: modifier,
    type: isHealing ? 'Healing' : (spell.damageType || 'Damage'),
    // [FIX]: Include secondary damage in result
    secondary: secondaryDamage
  };
};

/**
 * Consume the resource (spell slot or item charge)
 *
 * @param {Object} spell - The spell object
 * @param {number} castLevel - The level at which spell is being cast
 * @param {string} sourceType - 'spellbook' or 'item'
 * @param {string} sourceId - Item ID if casting from item
 * @param {Object} context - Context with setSpellSlots, setInventory, etc.
 */
export const consumeResource = (spell, castLevel, sourceType, sourceId, context) => {
  if (sourceType === 'item') {
    // Consume item charge
    const { setInventory, spellIndex, inventory } = context;
    const item = inventory?.find(i => i.id === sourceId);
    const spellData = item?.spells?.[spellIndex];

    // Determine if this is a shared-pool item (has charges + spell.costPerCast)
    const isPoolBased = item != null &&
      item.charges != null && item.maxCharges != null &&
      spellData != null && spellData.costPerCast != null;

    if (isPoolBased) {
      // Pool-based: deduct costPerCast from shared item.charges
      const cost = spellData.costPerCast;
      setInventory(prevInventory =>
        prevInventory.map(invItem => {
          if (invItem.id !== sourceId) return invItem;
          return { ...invItem, charges: Math.max(0, invItem.charges - cost) };
        })
      );

      // [INTEGRATION]: Emit ITEM_ABILITY_USED event
      if (item) {
        systemBus.emit(EVENTS.ITEM_ABILITY_USED, {
          item,
          abilityId: `spell_${spellIndex}`,
          chargesUsed: cost,
          chargesRemaining: Math.max(0, item.charges - cost),
          cooldown: null
        });
      }
    } else {
      // Legacy per-spell counter path (backward-compat)
      setInventory(prevInventory =>
        prevInventory.map(invItem => {
          if (invItem.id !== sourceId) return invItem;

          const spells = invItem.spells || [];
          const updatedSpells = spells.map((s, index) => {
            if (index === spellIndex) {
              return { ...s, currentUses: (s.currentUses ?? 0) - 1 };
            }
            return s;
          });

          return { ...invItem, spells: updatedSpells };
        })
      );

      // [INTEGRATION]: Emit ITEM_ABILITY_USED event (legacy path)
      if (item) {
        systemBus.emit(EVENTS.ITEM_ABILITY_USED, {
          item,
          abilityId: `spell_${spellIndex}`,
          chargesUsed: 1,
          chargesRemaining: (spellData?.currentUses || 1) - 1,
          cooldown: null
        });
      }
    }
  } else if (spell.level > 0) {
    // Consume spell slot (skip for cantrips)
    const { setSpellSlots, spellSlots } = context;
    const currentSlots = spellSlots?.[castLevel] || { max: 0, used: 0 };
    const slotsRemaining = currentSlots.max - (currentSlots.used + 1);

    setSpellSlots(prev => ({
      ...prev,
      [castLevel]: {
        ...(prev[castLevel] || {max: 0, used: 0}),
        used: (prev[castLevel]?.used || 0) + 1
      }
    }));

    // [INTEGRATION]: Emit SPELL_SLOT_USED event
    systemBus.emit(EVENTS.SPELL_SLOT_USED, {
      level: castLevel,
      slotsRemaining,
      spell: spell.name,
      source: 'manual_cast'
    });
  }
};

/**
 * Roll multi-attack for spells like Eldritch Blast
 *
 * @param {Object} spell - The spell object
 * @param {Object} context - Context with rollSpellAttack, playerStats, etc.
 * @returns {Array} - Array of attack results
 */
export const rollMultiAttack = (spell, context) => {
  const { rollSpellAttack, playerStats } = context;

  if (!spell.multiAttack || !spell.attacksPerLevel) {
    return null;
  }

  const numAttacks = spell.attacksPerLevel[Math.min(playerStats.level - 1, 19)] || 1;
  const attacks = [];

  for (let i = 0; i < numAttacks; i++) {
    const attack = rollSpellAttack();
    attacks.push(attack);
  }

  return attacks;
};

/**
 * Main spell casting function
 *
 * Orchestrates the entire casting process:
 * 1. Check if can cast
 * 2. Handle components (purchase/consume)
 * 3. Roll attack/damage
 * 4. Consume resource (UNLESS casting as ritual)
 * 5. Log activity
 * 6. Show status
 *
 * @param {Object} spell - The spell object
 * @param {Object} options - { castLevel, sourceType, sourceId, spellIndex, castAsRitual }
 * @param {Object} context - Context with all necessary functions and state
 * @returns {Object} - { success, message, results }
 */
export const castSpell = async (spell, options, context) => {
  const {
    castLevel = spell.level,
    sourceType = 'spellbook', // 'spellbook' or 'item'
    sourceId = null, // Item ID if casting from item
    spellIndex = 0, // Spell index if casting from item
    castAsRitual = false, // [NEW NOV 13, 2025]: Ritual casting (PHB p.114, p.201)
    isAtWill = false, // [Phase 4b-CW APR 2026]: At-will spell — bypass slot consumption
    // [Phase 4b BC-13 follow-up — APR 2026]: When set, the cast spends a
    //   per-rest free use (decrements playerStats._perRestSpellUses[spellName])
    //   instead of a spell slot. Mutually exclusive with isAtWill.
    usePerRestUse = false,
    perRestSpellMaxUses = 1, // matches the producer's `uses` field
    // [Phase 4b BC-13 closeout — APR 2026]: Resource-cost cast — spend N
    //   classResources points (e.g., 2 ki for Way of Shadow Darkness; 2
    //   sorcery points for Eyes of the Dark Darkness). Skips slot
    //   consumption AND decrements the resource via spendResource.
    //   Shape: { resource: 'ki_points', cost: 2 }. Falsy = not used.
    useResourceCost = null,
  } = options;

  const {
    showStatus,
    logActivity,
    getSpellSaveDC,
    getSpellAttackBonus
  } = context;

  // 1. Validate ritual casting
  // [PHB p.201]: Only spells with ritual tag can be cast as rituals
  if (castAsRitual && !spell.ritual) {
    const errorMsg = `${spell.name} cannot be cast as a ritual (no ritual tag)`;
    showStatus(errorMsg);
    return { success: false, message: errorMsg };
  }

  // [PHB p.201]: Ritual spells cannot be upcast
  if (castAsRitual && castLevel > spell.level) {
    const errorMsg = 'Ritual spells cannot be upcast - must use base level';
    showStatus(errorMsg);
    return { success: false, message: errorMsg };
  }

  // 2. Check if can cast
  const canCastResult = canCastSpell(spell, {
    ...context,
    castLevel,
    item: sourceType === 'item' ? context.inventory?.find(i => i.id === sourceId) : null,
    spellIndex,
    castAsRitual, // Pass ritual flag to canCastSpell for slot checking
    // [Phase 4b BC-13 follow-up]: A per-rest free cast also bypasses the slot
    //   check — same plumbing that `isAtWill` uses. canCastSpell only knows
    //   about isAtWill, so collapse usePerRestUse into the same flag for the
    //   pre-flight check.
    // [Phase 4b BC-13 closeout]: Same for resource-cost casts.
    isAtWill: isAtWill || usePerRestUse || !!useResourceCost,
  });

  if (!canCastResult.canCast) {
    showStatus(canCastResult.message);
    return { success: false, message: canCastResult.message };
  }

  // 2. Handle components (purchase/consume) - skip for cantrips
  if (spell.level > 0) {
    const componentsHandled = await handleComponents(
      spell,
      canCastResult.componentCheck,
      context
    );

    if (!componentsHandled) {
      return { success: false, message: 'Component purchase cancelled' };
    }
  }

  // 3. Roll attack/damage
  let attackDetails = null;
  let damageDetails = null;
  let rollResult = '';

  // Handle multi-attack (e.g., Eldritch Blast)
  if (spell.multiAttack && spell.saveType === 'attack') {
    const attacks = rollMultiAttack(spell, context);
    if (attacks) {
      rollResult = ` | ${attacks.length} attacks: ${attacks.map(a => a.total).join(', ')}`;
      attackDetails = {
        multiAttack: true,
        attacks: attacks.map(a => ({
          d20: a.roll,
          bonus: a.bonus,
          total: a.total,
          breakdown: `d20(${a.roll}) + ${a.bonus - (a.quickBonus || 0)} base${a.quickBonusText || ''}`
        }))
      };
    }
  }
  // Single attack roll
  else if (spell.saveType === 'attack') {
    const attack = rollSpellAttack(context);
    rollResult = ` | ${attack.text}`;
    attackDetails = {
      d20: attack.roll,
      bonus: attack.bonus,
      total: attack.total,
      breakdown: `d20(${attack.roll}) + ${attack.bonus - (attack.quickBonus || 0)} base${attack.quickBonusText || ''}`
    };
  }
  // Save DC
  else if (spell.saveType) {
    rollResult = ` | DC ${getSpellSaveDC()} ${spell.saveType} save`;
  }

  // Roll damage/healing
  if (spell.damage || spell.healing) {
    damageDetails = rollSpellDamage(spell, castLevel, context);
    if (damageDetails) {
      const isHealing = damageDetails.type === 'Healing';
      rollResult += ` | ${isHealing ? '💚' : '💥'} ${damageDetails.total} ${damageDetails.type}`;

      // [FIX]: Show secondary damage type if present (Issue 8)
      if (damageDetails.secondary) {
        const isHealing2 = damageDetails.secondary.type === 'Healing';
        rollResult += ` + ${isHealing2 ? '💚' : '💥'} ${damageDetails.secondary.total} ${damageDetails.secondary.type}`;
      }
    }
  }

  // 4. Consume resource (slot, per-rest free use, or item charge)
  // [RITUAL CASTING NOV 13, 2025]: Skip resource consumption if casting as ritual (PHB p.201)
  // [Phase 4b-CW APR 2026]: At-will spells (Warlock invocations like beast_speech) skip
  //   slot consumption — but item charges still consumed if cast from an item.
  //   Ritual casting does NOT consume spell slots (but still consumes item charges if from item)
  // [Phase 4b BC-13 follow-up — APR 2026]: Per-rest free use (Star Map's
  //   Guiding Bolt 1/long-rest, Walker in Dreams Dream/Scrying/Teleportation
  //   Circle) skips slot consumption AND decrements playerStats._perRestSpellUses
  //   so the next cast attempt uses a real slot.
  // [SPR-002 FIX — MAY 2026]: Spell scrolls do NOT consume the player's spell
  //   slots (DMG p.200 — "the spell is cast at its base level using the scroll's
  //   own magic"). The scroll itself is consumed in App.jsx line ~4328 after
  //   cast, regardless of success/failure. Pre-fix, casting a scroll burned BOTH
  //   the scroll AND a player slot of matching level — RAW violation.
  const skipSlotConsumption =
    (castAsRitual || isAtWill || usePerRestUse || !!useResourceCost || sourceType === 'scroll')
    && sourceType !== 'item';
  if (!skipSlotConsumption) {
    consumeResource(spell, castLevel, sourceType, sourceId, {
      ...context,
      spellIndex
    });
  }
  if (usePerRestUse && context.setPlayerStats) {
    context.setPlayerStats(prev => consumePerRestSpellUse(prev, spell.name, perRestSpellMaxUses));
  }
  // [Phase 4b BC-13 closeout — APR 2026]: Resource-cost cast decrements via
  //   spendResource. Falls back to a no-op when the resource isn't found or
  //   the player can't afford it (canCastSpell should have rejected first,
  //   but defense in depth doesn't hurt).
  if (useResourceCost && useResourceCost.resource && useResourceCost.cost > 0 && context.setPlayerStats) {
    context.setPlayerStats(prev => spendResource(prev, useResourceCost.resource, useResourceCost.cost));
  }

  // 4b. Consume material components flagged by CustomSpellCreator's
  //     "Materials consumed" toggle (componentDetails.consumed = true).
  //     [Tier 1.15 — APR 2026]: Previously this flag was persisted but never
  //     honored — the form was lying to the user. The helper is a no-op when
  //     the flag is false / missing.
  //
  //     Skipped components (flag set, item not in inventory) surface a
  //     non-blocking warning toast so the player can self-track. The cast
  //     itself still proceeds.
  let componentConsumptionResult = null;
  // [SPR-006 FIX — MAY 2026]: Honor settings.enforceComponentConsumption.
  //   Pre-fix the Tier 1.15 helper was unconditional — players who turned
  //   the toggle OFF still had their custom-spell components consumed
  //   (mismatch with the legacy SRD path at App.jsx:4490 which does honor
  //   the setting). Now both paths respect the toggle.
  const enforceComponents = context?.settings?.enforceComponentConsumption !== false;
  if (
    spell.componentDetails &&
    spell.componentDetails.consumed &&
    context.setInventory &&
    enforceComponents
  ) {
    try {
      componentConsumptionResult = consumeMaterialComponents(
        spell,
        context.inventory || [],
        context.setInventory,
        logActivity
      );
      // Show a non-blocking warning when a flagged component was missing.
      if (componentConsumptionResult.skipped.length > 0) {
        const names = componentConsumptionResult.skipped.map(s => s.name).join(', ');
        showStatus(`⚠ Material component '${names}' not in inventory — track manually.`, 4000);
      }
    } catch (err) {
      console.warn(`[castSpell] component consumption failed for ${spell.name}`, err);
    }
  }

  // 5. Log activity
  const logData = {
    spell: spell.name,
    level: castLevel,
    school: spell.school,
    ritual: castAsRitual, // [NEW NOV 13, 2025]: Track ritual casting
    attackRoll: attackDetails,
    saveDC: spell.saveType && spell.saveType !== 'attack' ? getSpellSaveDC() : null,
    damage: damageDetails ? {
      total: damageDetails.total,
      formula: damageDetails.dice,
      type: damageDetails.type,
      diceGroups: damageDetails.diceGroups,
      modifier: damageDetails.modifier,
      // [FIX]: Include secondary damage in log (Issue 8)
      secondary: damageDetails.secondary ? {
        total: damageDetails.secondary.total,
        formula: damageDetails.secondary.dice,
        type: damageDetails.secondary.type
      } : null
    } : null
  };

  // [RITUAL CASTING NOV 13, 2025]: Show ritual casting in log message
  // [Phase 4b-CW APR 2026]: Show "at will (no slot)" for at-will spells
  const castMessage = spell.level === 0 ?
    `Cast ${spell.name} (Cantrip)` :
    castAsRitual ?
      `Cast ${spell.name} as Ritual (+10 min, no slot)` :
      isAtWill ?
        `Cast ${spell.name} at will (no slot)` :
        `Cast ${spell.name} at level ${castLevel}`;

  logActivity(castMessage, 'spells', logData);

  // 6. Show status
  if (spell.saveType === 'attack' && attackDetails && !attackDetails.multiAttack) {
    showStatus(`🎯 Attack Roll: ${attackDetails.total}`, 3000, attackDetails);
  } else {
    showStatus(`🎯 ${spell.name} cast${rollResult}`, 3000, damageDetails);
  }

  // [INTEGRATION]: Emit SPELL_CAST event for other systems to react
  systemBus.emit(EVENTS.SPELL_CAST, {
    spell: {
      name: spell.name,
      level: spell.level,
      school: spell.school,
      concentration: spell.concentration || false,
      ritual: spell.ritual || false
    },
    level: castLevel,
    upcast: castLevel > spell.level,
    castAsRitual: castAsRitual, // [NEW NOV 13, 2025]: Track if cast as ritual
    target: null, // Could be enhanced to track target
    components: {
      verbal: spell.components?.includes('V') || false,
      somatic: spell.components?.includes('S') || false,
      material: spell.components?.includes('M') || false
    },
    result: {
      attackDetails,
      damageDetails,
      success: true
    },
    source: sourceType,
    sourceId: sourceType === 'item' ? sourceId : null
  });

  // [PHASE 0.2 - NOV 11, 2025]: Start concentration tracking if concentration spell
  // [WHY]: D&D 5e allows only one concentration spell at a time
  if (spell.concentration) {
    // Get character object from context
    const character = context.character || context.playerStats || { name: 'Unknown' };

    // Start concentration (automatically breaks previous concentration if any)
    concentrationSystem.startConcentration(
      spell,
      character,
      showStatus,
      logActivity
    );

    // Still emit legacy event for backward compatibility
    systemBus.emit(EVENTS.CONCENTRATION_STARTED, {
      spell: {
        name: spell.name,
        level: castLevel,
        school: spell.school
      },
      duration: spell.duration || 'Concentration',
      effects: spell.description || null
    });
  }

  // [Phase 4b CW-parity (C) — APR 2026]: Register the spell's mechanical effects
  //   into effectRegistry. spellEffects is the registry-shape block emitted by
  //   the CW Spell Effect Builder (or by SRD spell data when wired). This is
  //   the same shape used by srdSubclassFeatures.js / srdClassFeatures.js so
  //   the registry consumes them uniformly.
  //
  //   Cleanup:
  //   - Concentration spells: concentrationSystem.breakConcentration retracts
  //     by prefix when the next concentration cast displaces this one (or when
  //     the player breaks concentration manually).
  //   - Non-concentration spells: registered with a manual sourceId; player or
  //     calendar-tick / spell-expiry hook can retract via effectRegistry.remove.
  //     For the MVP, non-concentration spell effects persist until manually
  //     removed (player can dismiss via SpellsPanel "expire" once UI lands).
  if (spell.spellEffects && (spell.spellEffects.effects?.length > 0 || spell.spellEffects.choices)) {
    const character = context.character || context.playerStats || {};
    const castStamp = Date.now();
    const sourceIdBase = `spell:cast:${spell.name}:${castStamp}`;
    // [Phase 4b CW-parity (C) follow-up — APR 2026]: Attach duration metadata
    //   so spellEffectExpiry can retract non-concentration effects on rest /
    //   day-pass events. Concentration spells skip the duration parser
    //   (concentrationSystem owns their lifecycle).
    const durationMinutes = spell.concentration
      ? null
      : parseDurationToMinutes(spell.duration);
    const castMetadata = {
      spellName: spell.name,
      castLevel,
      durationMinutes,
      concentration: !!spell.concentration,
      castStamp,
    };

    // Always-on cast effects
    if (Array.isArray(spell.spellEffects.effects) && spell.spellEffects.effects.length) {
      try {
        // metadata is the 3rd arg of register() — used by spell-stacking +
        // by spellEffectExpiry to find sources whose duration has elapsed.
        effectRegistry.register(sourceIdBase, spell.spellEffects.effects, castMetadata);
      } catch (err) {
        console.warn(`[castSpell] failed to register effects for ${spell.name}`, err);
      }
    }

    // Choice-dependent cast effects (uncommon for spells, but supported for
    // parity with the feature-side schema)
    if (spell.spellEffects.choices && spell.spellEffects.choiceKey) {
      const selected = (spell.spellEffects.choiceKey || '').split('.')
        .reduce((acc, k) => (acc != null ? acc[k] : undefined), character);
      const optionData = selected ? spell.spellEffects.choices[selected] : null;
      if (optionData && Array.isArray(optionData.effects) && optionData.effects.length) {
        try {
          effectRegistry.register(`${sourceIdBase}:${selected}`, optionData.effects, castMetadata);
        } catch (err) {
          console.warn(`[castSpell] failed to register choice effects for ${spell.name}:${selected}`, err);
        }
      }
    }

    // Stash the source-id base so concentrationSystem can clean up on end.
    // Non-concentration spell effects are cleaned up by spellEffectExpiry
    // (rest / dawn events) — see src/services/spellEffectExpiry.js.
    if (spell.concentration) {
      concentrationSystem.attachEffectsSourceId?.(sourceIdBase);
    }

    effectCalculator.clearCache();
  }

  return {
    success: true,
    message: `${spell.name} cast successfully`,
    results: {
      attackDetails,
      damageDetails,
      castLevel
    }
  };
};

/**
 * [Spec B Task 13 — 2026-05-16, lifted 2026-05-16 evening per Rule 11]:
 *   Cast a spell stored in a bound item via the `longRestPicker` mechanism
 *   with `pickerId: 'spell_storing_item'`. SRD uses Artificer Spell-Storing
 *   Item (TCoE p.11); ANY class — SRD or CW custom-class — that declares
 *   this picker uses the same path. No literal class string in the lookup.
 *
 * State layout (data-driven):
 *   - Active selections under `playerStats.classActiveSelections[classKey].spell_storing_item`
 *     are an array of `{ name, boundItemId, ... }` entries.
 *   - Charges live under `playerStats.classes[i].spell_storing_item.current`
 *     for the class that owns the binding.
 *
 * Contract:
 *   - Find the binding for `boundItemId` by scanning ALL classes' active
 *     selections (Object.entries over classActiveSelections). First match wins.
 *   - If none, no-op.
 *   - If owning class's pool current ≤ 0, no-op.
 *   - Decrement the OWNING class's pool by 1 via setPlayerStats (not a
 *     literal class string).
 *   - Log activity with classKey for traceability.
 *   - v1 — actual spell-effect roll (damage / save) deferred to player
 *     adjudication; combat-tracker integration is the v1.1 follow-up.
 *
 * @param {Object} playerStats
 * @param {Array} inventory
 * @param {string} boundItemId — the inventory item id
 * @param {Object} ctx — { setPlayerStats, logActivity }
 * @returns {Object} { success, message, spellName?, chargesAfter?, classKey? }
 */
export function castStoredSpell(playerStats, inventory, boundItemId, ctx = {}) {
  if (!playerStats || !boundItemId) {
    return { success: false, message: 'castStoredSpell: missing playerStats or boundItemId.' };
  }

  // [LIFT 2026-05-16 — Rule 11]: Scan all classes' classActiveSelections for the
  // binding. Any class that declares a longRestPicker with pickerId
  // 'spell_storing_item' participates — no literal class string.
  const allActiveSelections = playerStats.classActiveSelections || {};
  let bindingClassKey = null;
  let selection = null;
  for (const [classKey, classSels] of Object.entries(allActiveSelections)) {
    const active = classSels?.spell_storing_item;
    if (Array.isArray(active)) {
      const match = active.find(s => s && s.boundItemId === boundItemId);
      if (match) {
        bindingClassKey = classKey;
        selection = match;
        break;
      }
    }
  }
  if (!selection || !bindingClassKey) {
    return { success: false, message: 'No spell stored in this item.' };
  }

  // Find the owning class instance on the character (data-driven match on classKey).
  const ownerClass = (playerStats.classes || []).find(c => c?.className === bindingClassKey);
  if (!ownerClass) {
    return { success: false, message: `Owning class "${bindingClassKey}" missing from character.` };
  }

  // Pool key: snake_case primary, camelCase legacy fallback.
  const poolKey = ownerClass.spell_storing_item != null
    ? 'spell_storing_item'
    : (ownerClass.spellStoringItem != null ? 'spellStoringItem' : 'spell_storing_item');
  const pool = ownerClass[poolKey] || { current: 0, max: 0 };
  if ((pool.current || 0) <= 0) {
    return { success: false, message: 'No charges remaining in the stored spell.' };
  }

  // Decrement the OWNING class's pool (data-driven, not a literal class string).
  if (typeof ctx.setPlayerStats === 'function') {
    ctx.setPlayerStats(prev => {
      const nextClasses = (prev.classes || []).map(cls => {
        if (cls?.className !== bindingClassKey) return cls;
        const cur = cls[poolKey] || { current: 0, max: pool.max };
        return { ...cls, [poolKey]: { ...cur, current: Math.max(0, (cur.current || 0) - 1) } };
      });
      return { ...prev, classes: nextClasses };
    });
  }

  if (typeof ctx.logActivity === 'function') {
    ctx.logActivity(
      `Cast ${selection.name} from stored-spell item`,
      'spell',
      {
        source: 'spell_storing_item',
        spellName: selection.name,
        boundItemId,
        classKey: bindingClassKey,
      },
    );
  }

  return {
    success: true,
    message: `Cast ${selection.name} from stored-spell item.`,
    spellName: selection.name,
    chargesAfter: Math.max(0, (pool.current || 0) - 1),
    classKey: bindingClassKey,
  };
}

// Export all functions
export default {
  canCast,
  canCastSpell,
  handleComponents,
  calculateUpcastDamage,
  rollSpellAttack,
  rollSpellDamage,
  consumeResource,
  rollMultiAttack,
  castSpell,
  castStoredSpell,
};
